import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Award,
  Lock,
  Check,
  Gift,
  Target,
  Trophy,
  Crown,
  Skull,
  Sparkles,
  Repeat,
  Plane,
  Waves,
  Clock,
  Zap,
  Coins,
  Star,
} from "lucide-react";
import { playClick, playWin } from "../game/sound";
import { useAuth } from "../contexts/AuthContext";
import { fetchAchievementsCatalog, claimAchievement } from "../game/authService";

const ICONS = {
  Zap, Repeat, Plane, Target, Trophy, Waves, Crown, Skull, Sparkles, Clock,
};

const STAT_LABELS = {
  total_zombies: "zombies",
  total_games: "runs",
  highest_wave: "wave",
  bosses_defeated: "bosses",
  total_play_seconds: "seconds played",
  account_level: "level",
};

function progressFor(ach, user) {
  if (ach.stat === "account_level") return user?.level || 1;
  return (user?.stats || {})[ach.stat] || 0;
}

function rewardLine(reward) {
  const parts = [];
  if (reward.coins) parts.push({ icon: Coins, label: `${reward.coins} coins` });
  if (reward.xp) parts.push({ icon: Sparkles, label: `${reward.xp} XP` });
  if (reward.talent_points) parts.push({ icon: Star, label: `${reward.talent_points} TP` });
  if (reward.cosmetic_tokens) parts.push({ icon: Gift, label: `${reward.cosmetic_tokens} Cosmetic Token` });
  if (reward.gems) parts.push({ icon: Gift, label: `${reward.gems} Gems` });
  return parts;
}

function AchievementCard({ ach, user, busy, onClaim }) {
  const Icon = ICONS[ach.icon] || Award;
  const state = (user?.achievements || {})[ach.id] || {};
  const unlocked = !!state.unlocked;
  const claimed = !!state.claimed;
  const progress = progressFor(ach, user);
  const pct = Math.min(100, (progress / ach.threshold) * 100);
  const status = claimed ? "claimed" : unlocked ? "unlocked" : "locked";
  const rewards = rewardLine(ach.reward || {});

  return (
    <div
      className={`ach-card ach-${status}`}
      data-testid={`ach-card-${ach.id}`}
    >
      <div className="ach-card-head">
        <div className="ach-card-icon">
          <Icon size={20} />
        </div>
        <div className="ach-card-titles">
          <div className="ach-card-title">{ach.name}</div>
          <div className="ach-card-desc">{ach.desc}</div>
        </div>
        {status === "claimed" && (
          <div className="ach-status-pill claimed">
            <Check size={12} /> Claimed
          </div>
        )}
        {status === "unlocked" && (
          <div className="ach-status-pill unlocked">
            <Sparkles size={12} /> Ready
          </div>
        )}
        {status === "locked" && (
          <div className="ach-status-pill locked">
            <Lock size={12} /> Locked
          </div>
        )}
      </div>

      <div className="ach-progress" data-testid={`ach-progress-${ach.id}`}>
        <div className="ach-progress-bar">
          <div className="ach-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="ach-progress-label">
          {Math.min(progress, ach.threshold).toLocaleString()} / {ach.threshold.toLocaleString()}
          <span className="ach-progress-stat"> {STAT_LABELS[ach.stat] || ach.stat}</span>
        </span>
      </div>

      <div className="ach-card-foot">
        <div className="ach-rewards">
          {rewards.map((r, i) => {
            const RIcon = r.icon;
            return (
              <span key={i} className="ach-reward-chip">
                <RIcon size={12} /> {r.label}
              </span>
            );
          })}
        </div>
        {status === "unlocked" && (
          <button
            type="button"
            className="zc-btn primary ach-claim"
            disabled={busy}
            onClick={() => onClaim(ach.id)}
            data-testid={`ach-claim-${ach.id}`}
          >
            <Gift size={14} /> {busy ? "..." : "Claim"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function AchievementsScreen({ onBack }) {
  const { user, refresh } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  const [flashId, setFlashId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchAchievementsCatalog().then((list) => {
      if (!cancelled) setCatalog(list || []);
    });
    return () => { cancelled = true; };
  }, []);

  const counts = useMemo(() => {
    const state = user?.achievements || {};
    let unlocked = 0;
    let claimed = 0;
    catalog.forEach((a) => {
      const s = state[a.id] || {};
      if (s.unlocked) unlocked += 1;
      if (s.claimed) claimed += 1;
    });
    return { unlocked, claimed, total: catalog.length };
  }, [catalog, user]);

  // Sort: unlocked-unclaimed (claimable) first, then locked, then claimed.
  const sorted = useMemo(() => {
    const order = (a) => {
      const s = (user?.achievements || {})[a.id] || {};
      if (s.unlocked && !s.claimed) return 0;
      if (!s.unlocked) return 1;
      return 2;
    };
    return [...catalog].sort((a, b) => order(a) - order(b));
  }, [catalog, user]);

  const claim = async (id) => {
    setErr("");
    setBusy(id);
    playClick();
    try {
      await claimAchievement(id);
      await refresh();
      playWin();
      setFlashId(id);
      setTimeout(() => setFlashId(null), 1200);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not claim");
    } finally {
      setBusy(null);
    }
  };

  if (!user) {
    return (
      <div className="screen placeholder-screen" data-testid="achievements-screen">
        <button
          type="button"
          className="zc-btn icon-back"
          onClick={() => { playClick(); onBack(); }}
        >
          <ArrowLeft size={18} /> Back
        </button>
        <div className="placeholder-wrap">
          <h1 className="placeholder-title">Achievements</h1>
          <p className="placeholder-blurb">
            Sign in with Google to track and claim achievement rewards.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="screen achievements-screen" data-testid="achievements-screen">
      <button
        type="button"
        className="zc-btn icon-back"
        onClick={() => { playClick(); onBack(); }}
      >
        <ArrowLeft size={18} /> Back
      </button>

      <div className="ach-wrap">
        <h1 className="ach-title">
          <Award size={26} /> Achievements
        </h1>
        <p className="ach-tag">
          Hit lifetime milestones and claim coins, XP, talent points and cosmetics.
          Progress tracks automatically every run.
        </p>

        <div className="ach-header" data-testid="ach-header">
          <div className="ach-header-stat">
            <span className="ach-header-label">Unlocked</span>
            <span className="ach-header-value" data-testid="ach-unlocked-count">
              {counts.unlocked} / {counts.total}
            </span>
          </div>
          <div className="ach-header-stat">
            <span className="ach-header-label">Claimed</span>
            <span className="ach-header-value" data-testid="ach-claimed-count">
              {counts.claimed}
            </span>
          </div>
          <div className="ach-header-stat">
            <span className="ach-header-label">Completion</span>
            <span className="ach-header-value">
              {counts.total > 0 ? Math.round((counts.claimed / counts.total) * 100) : 0}%
            </span>
          </div>
        </div>

        <div className="ach-grid" data-testid="ach-grid">
          {sorted.map((ach) => (
            <div
              key={ach.id}
              className={flashId === ach.id ? "ach-flash" : ""}
            >
              <AchievementCard
                ach={ach}
                user={user}
                busy={busy === ach.id}
                onClaim={claim}
              />
            </div>
          ))}
        </div>

        {err && <div className="vip-err" data-testid="ach-err">{err}</div>}
      </div>
    </div>
  );
}
